import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Box, IconButton, TextField, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../app/hooks";
import ActionMenu from "../../../common/components/functional/actionMenu/ActionMenu";
import { CustomButton } from "../../../common/components/functional/customButton/CustomButton";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import DialogContentWrapper from "../../../common/components/functional/dialogs/DialogContentWrapper";
import { getTalentStatusIcon } from "../../../common/components/functional/talentStatus/TalentStatusIcons";
import { ReactComponent as CalendarIcon } from "../../../common/icons/calendar-small.svg";
import { ReactComponent as CheckIcon } from "../../../common/icons/checkGreen.svg";
import { ReactComponent as LaterIcon } from "../../../common/icons/user-block-filled.svg";
import { Nullable } from "../../../common/types/nullable";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { PoolProPopper } from "../../talents/PoolProPopper";
import { hasPoolPro } from "../../talents/helpers";
import { Talent, talentIsDisabled } from "../../talents/talentAPI";
import { updateTalentStatus } from "../../talents/talentsSlice";
import { TalentStatus } from "../../talents/types/common";
import { TalentChangeLog } from "../changeLogs/TalentChangeLog";
import { addTalentComment } from "../comments/TalentCommentsSlice";

type Props = {
  talent: Talent;
};

const TalentProfileHeader = ({ talent }: Props) => {
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);

  const { t } = useTranslation("talentProfile");
  const dispatch = useAppDispatch();
  const { user } = useCurrentUser();

  const [isChangeLogDialogOpen, setIsChangeLogDialogOpen] = useState(false);
  const [reasonDialogStatus, setReasonDialogStatus] =
    useState<Nullable<TalentStatus>>(null);
  const [statusReason, setStatusReason] = useState("");
  const [isSavingStatusReason, setIsSavingStatusReason] = useState(false);

  const onCloseChangeLogDialog = () => {
    setIsChangeLogDialogOpen(false);
  };

  const talentFullName = `${talent?.account.firstName} ${talent?.account.lastName}`;
  const icon = getTalentStatusIcon(talent.account?.status);
  const acceptTalentMenuDisabled =
    talent.hrRepresentative == null &&
    talent.account.status !== TalentStatus.ACTIVE;
  const acceptTalentMenuInfoText = acceptTalentMenuDisabled
    ? t("acceptNotAllowedWithoutHrRepresentative")
    : null;

  const formattedStatusChanged = talent.account?.statusChanged
    ? format(new Date(talent.account.statusChanged), "dd.MM.yyyy")
    : null;

  const dispatchUpdateTalentStatus = (status: TalentStatus) => {
    const payload = {
      talentId: talent.id,
      version: talent.account.version,
      status,
    };

    dispatch(updateTalentStatus(payload));
  };

  const handleUpdateTalentStatus = (key: string) => {
    if (key === "invite") dispatchUpdateTalentStatus(TalentStatus.INTERVIEW);
    if (key === "interviewPending")
      dispatchUpdateTalentStatus(TalentStatus.INTERVIEW_PENDING);
    if (key === "acceptWithoutInterview")
      dispatchUpdateTalentStatus(TalentStatus.ACTIVE);
    if (key === "invitedAndAccepted")
      dispatchUpdateTalentStatus(TalentStatus.ACTIVE);
    if (key === "later") openStatusReasonDialog(TalentStatus.LATER);
    if (key === "employedElsewhere") {
      openStatusReasonDialog(TalentStatus.EMPLOYED_ELSEWHERE);
    }
    if (key === "notEmployed")
      openStatusReasonDialog(TalentStatus.NOT_EMPLOYED);
  };

  const openStatusReasonDialog = (status: TalentStatus) => {
    setAnchorEl(null);
    setReasonDialogStatus(status);
  };

  const closeStatusReasonDialog = () => {
    setReasonDialogStatus(null);
    setStatusReason("");
  };

  const handleStatusReasonConfirm = async () => {
    if (!user || !reasonDialogStatus || !statusReason.trim()) return;

    setIsSavingStatusReason(true);

    const addCommentAction = await dispatch(
      addTalentComment({
        talentId: talent.id,
        comment: statusReason.trim(),
        authorId: user.id,
        authorFirstName: user.firstName,
        authorLastName: user.lastName,
        relatedStatus: reasonDialogStatus,
      }),
    );

    if (isFulfilled(addCommentAction)) {
      dispatchUpdateTalentStatus(reasonDialogStatus);
      closeStatusReasonDialog();
    }

    setIsSavingStatusReason(false);
  };

  const menuItems = [
    {
      key: "invite",
      label: t("invitedToInterview"),
      action: (key: string) => handleUpdateTalentStatus(key),
      icon: <CalendarIcon />,
    },
    {
      key: "interviewPending",
      label: t("interviewPendingAction"),
      action: (key: string) => handleUpdateTalentStatus(key),
      icon: <CalendarIcon />,
      textSx: { color: "#000" },
    },
    {
      key: "acceptWithoutInterview",
      label: t("acceptWithoutInterview"),
      action: (key: string) => handleUpdateTalentStatus(key),
      icon: <CheckIcon />,
      textSx: { color: "success.main" },
      disabled: acceptTalentMenuDisabled,
      infoTooltipText: acceptTalentMenuInfoText,
    },
    {
      key: "invitedAndAccepted",
      label: t("invitedAndAccepted"),
      action: (key: string) => handleUpdateTalentStatus(key),
      icon: <CheckIcon />,
      textSx: { color: "success.main" },
      disabled: acceptTalentMenuDisabled,
      infoTooltipText: acceptTalentMenuInfoText,
    },
    {
      key: "later",
      label: t("LATER"),
      action: (key: string) => handleUpdateTalentStatus(key),
      icon: <LaterIcon />,
      textSx: { color: "warning.main" },
    },
    {
      key: "employedElsewhere",
      label: t("EMPLOYED_ELSEWHERE"),
      action: (key: string) => handleUpdateTalentStatus(key),
      icon: <LaterIcon />,
      textSx: { color: "warning.main" },
    },
    {
      key: "notEmployed",
      label: t("NOT_EMPLOYED"),
      action: (key: string) => handleUpdateTalentStatus(key),
      icon: <LaterIcon />,
      textSx: { color: "warning.main" },
    },
  ];

  const onOpen = (event: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(event.currentTarget);

  const onClose = () => setAnchorEl(null);

  const buildStatusText = (talent: Talent): string => {
    const { status, gracePeriodEndDate, redactionDate } = talent.account || {};

    switch (status) {
      case TalentStatus.DISABLED: {
        let statusText = formattedStatusChanged
          ? t("DISABLED_statusText", { date: formattedStatusChanged })
          : t("DISABLED");

        return gracePeriodEndDate && redactionDate
          ? `${statusText}. ${t("gracePeriodStarted", { date1: format(new Date(gracePeriodEndDate), "dd.MM.yyyy"), date2: format(new Date(redactionDate), "dd.MM.yyyy") })}`
          : statusText;
      }
      case TalentStatus.DEACTIVATED: {
        let statusText = formattedStatusChanged
          ? t("DEACTIVATED_statusText", {
              date: formattedStatusChanged,
            })
          : t("DEACTIVATED");

        return redactionDate
          ? `${statusText}. ${t("gracePeriodEnded", { date: format(new Date(redactionDate), "dd.MM.yyyy") })}`
          : statusText;
      }
      default:
        return "";
    }
  };

  const getTalentStatusProps = () => {
    switch (talent.account?.status) {
      case TalentStatus.INCOMPLETE:
      case TalentStatus.NEW:
      case TalentStatus.INTERVIEW_PENDING:
      case TalentStatus.INTERVIEW:
        return {
          textColor: "inherit",
          iconButtonColor: "transparent",
          iconButtonHoverColor: "#0000000D",
          containerColor: "#FFBE6180",
          text: formattedStatusChanged
            ? t(`${talent.account.status}_statusText`, {
                date: formattedStatusChanged,
              })
            : t(`${talent.account.status}_long`),
          moreIcon: <MoreHorizIcon sx={{ color: "#000000" }} />,
        };
      case TalentStatus.ACTIVE:
        return {
          textColor: "inherit",
          iconButtonColor: "primary.main",
          iconButtonHoverColor: "#1D095A",
          containerColor: "transparent",
          text: formattedStatusChanged
            ? t("ACTIVE_statusText", { date: formattedStatusChanged })
            : t("ACTIVE"),
          moreIcon: <MoreHorizIcon sx={{ color: "#fff" }} />,
        };
      case TalentStatus.LATER:
        return {
          textColor: "#fff",
          iconButtonColor: "transparent",
          iconButtonHoverColor: "#ffffff33",
          containerColor: "#FF9600",
          text: formattedStatusChanged
            ? t(`${talent.account.status}_statusText`, {
                date: formattedStatusChanged,
              })
            : t(talent.account.status),
          moreIcon: <MoreHorizIcon sx={{ color: "#fff" }} />,
          iconColor: "text.secondary",
        };
      case TalentStatus.EMPLOYED_ELSEWHERE:
      case TalentStatus.NOT_EMPLOYED:
        return {
          textColor: "#fff",
          iconButtonColor: "transparent",
          iconButtonHoverColor: "#ffffff33",
          containerColor: "#FF9600",
          text: formattedStatusChanged
            ? t(`${talent.account.status}_statusText`, {
                date: formattedStatusChanged,
              })
            : t(talent.account.status),
          moreIcon: <MoreHorizIcon sx={{ color: "#fff" }} />,
        };
      case TalentStatus.DISABLED:
      case TalentStatus.DEACTIVATED:
        return {
          textColor: "#fff",
          iconButtonColor: "transparent",
          iconButtonHoverColor: "#ffffff33",
          containerColor: "#FF9600",
          text: buildStatusText(talent),
          moreIcon: <MoreHorizIcon sx={{ color: "#fff" }} />,
        };
      default:
        return {
          textColor: "inherit",
          iconButtonColor: "primary.main",
          iconButtonHoverColor: "#1D095A",
          containerColor: "transparent",
          text: "",
          moreIcon: <MoreHorizIcon sx={{ color: "#fff" }} />,
        };
    }
  };

  const talentStatusProps = getTalentStatusProps();

  return (
    <Box
      id="talent-profile-header"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      bgcolor={talentStatusProps.containerColor}
      width="calc(100% + 32px)"
      margin="-16px -16px 0px -16px"
      padding="10px 16px"
    >
      <Box>
        <Box display="flex" alignItems="center">
          <Typography
            id="talent-profile-header-talent-name"
            variant="header"
            sx={{
              fontSize: "32px",
              fontWeight: 600,
              color: talentStatusProps.textColor,
            }}
          >
            {talentFullName}
            <Box
              component="span"
              ml={1}
              sx={
                talentStatusProps.iconColor
                  ? {
                      "& .MuiSvgIcon-root": {
                        color: talentStatusProps.iconColor,
                      },
                    }
                  : undefined
              }
            >
              {icon}
            </Box>
          </Typography>
          {hasPoolPro(talent) && (
            <PoolProPopper
              labelOnly
              talent={talent}
              labelSx={{
                marginLeft: "10px",
                marginTop: "2px",
                fontSize: "18px",
              }}
            />
          )}
        </Box>
        <CustomButton
          sx={{ borderRadius: "24px", padding: "5px 15px", height: "37px" }}
          onClick={() => setIsChangeLogDialogOpen(true)}
        >
          {t("common:showLogs")}
        </CustomButton>
      </Box>
      <Box display="flex" alignItems="center" gap="16px">
        <Typography color={talentStatusProps.textColor}>
          {talentStatusProps.text}
        </Typography>
        <IconButton
          id="talent-profile-header-more-button"
          onClick={onOpen}
          sx={{
            width: "28px",
            height: "28px",
            backgroundColor: talentStatusProps.iconButtonColor,
            "&:hover": {
              backgroundColor: talentStatusProps.iconButtonHoverColor,
            },
          }}
        >
          {talentStatusProps.moreIcon}
        </IconButton>
      </Box>
      {!talentIsDisabled(talent) ? (
        <ActionMenu
          id="talent-profile-header"
          menuItems={menuItems}
          anchorEl={anchorEl}
          onClose={onClose}
        />
      ) : (
        <></>
      )}
      <TalentChangeLog
        talentId={talent.id}
        talentName={talentFullName}
        open={isChangeLogDialogOpen}
        onClose={onCloseChangeLogDialog}
      />
      <DialogComponent
        open={!!reasonDialogStatus}
        onClose={closeStatusReasonDialog}
        variant="form"
        autoWidth
      >
        <DialogContentWrapper
          title={reasonDialogStatus ? t(`${reasonDialogStatus}_long`) : ""}
          onCancel={closeStatusReasonDialog}
          onSave={handleStatusReasonConfirm}
          saveDisabled={!user || !statusReason.trim()}
          isLoading={isSavingStatusReason}
          customSaveButtonLabel={t("common:save")}
          childContainerSx={{
            width: "520px",
            paddingLeft: "16px",
            paddingTop: "16px",
          }}
        >
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            placeholder={t("statusReasonLabel")}
            value={statusReason}
            onChange={(event) => setStatusReason(event.target.value)}
            required
          />
        </DialogContentWrapper>
      </DialogComponent>
    </Box>
  );
};

export default TalentProfileHeader;
